import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectDataClient } from '../lib/connect/clients';
/**
 * Frontend hooks for the V2 ProjectVolume RPCs added in PR5.
 *
 * The backend returns the merged set of pipeline-derived volumes and any
 * user-created custom volumes, ordered by `sort_order ASC, display_name ASC`.
 * Custom volumes carry `isCustom = true` so the rail can render a badge.
 */ export const projectVolumesKeys = {
    all: [
        'projectVolumes'
    ],
    list: (projectId)=>[
            ...projectVolumesKeys.all,
            'list',
            projectId
        ]
};
export function useProjectVolumes(projectId) {
    return useQuery({
        queryKey: projectVolumesKeys.list(projectId ?? ''),
        queryFn: async ()=>{
            if (!projectId) return [];
            const response = await projectDataClient.listProjectVolumes({
                projectId
            });
            return response.volumes ?? [];
        },
        enabled: !!projectId,
        staleTime: 30000
    });
}
export function useCreateCustomVolume() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ projectId, displayName })=>{
            const response = await projectDataClient.createCustomVolume({
                projectId,
                displayName
            });
            return response.volume;
        },
        onSuccess: (_volume, { projectId })=>{
            queryClient.invalidateQueries({
                queryKey: projectVolumesKeys.list(projectId)
            });
        }
    });
}
export function useRenameVolume() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ projectId, volumeId, displayName })=>{
            const response = await projectDataClient.renameVolume({
                projectId,
                volumeId,
                displayName
            });
            return response.volume;
        },
        onSuccess: (_volume, { projectId })=>{
            queryClient.invalidateQueries({
                queryKey: projectVolumesKeys.list(projectId)
            });
        }
    });
}
export function useDeleteCustomVolume() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ projectId, volumeId })=>{
            await projectDataClient.deleteCustomVolume({
                projectId,
                volumeId
            });
        },
        onSuccess: (_void, { projectId })=>{
            queryClient.invalidateQueries({
                queryKey: projectVolumesKeys.list(projectId)
            });
        }
    });
}
